import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import PaymentModal from '../components/PaymentModal';
import Receipt from '../components/Receipt';
import ListFilters from '../components/ListFilters';
import Pagination from '../components/Pagination';
import { downloadCSV } from '../utils/downloadCSV';

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function BillingPage() {
  const { vendor } = useAuth();
  const [orders,      setOrders]      = useState([]);
  const [pagination,  setPagination]  = useState(null);
  const [page,        setPage]        = useState(1);
  const [dateRange,   setDateRange]   = useState({ startDate: todayStr(), endDate: todayStr() });
  const [billingFilters, setBillingFilters] = useState({ paymentMode: '', paymentStatus: '' });
  const [sel,         setSel]         = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [activeOrder, setActiveOrder] = useState(null);
  const [stats,       setStats]       = useState(null);
  const [feeStats,    setFeeStats]    = useState(null);
  const [confirming,  setConfirming]  = useState(null);
  const [cashError,   setCashError]   = useState('');

  const fetchOrders = useCallback(() => {
    if (!vendor?.id) return;
    const params = {
      page, limit: 50,
      startDate: dateRange.startDate,
      endDate:   dateRange.endDate,
      ...(billingFilters.paymentMode   && { paymentMode:   billingFilters.paymentMode }),
      ...(billingFilters.paymentStatus && { paymentStatus: billingFilters.paymentStatus }),
    };
    api.get('/orders', { params })
      .then(res => {
        const list = res.data.data || [];
        setOrders(list);
        setPagination(res.data.pagination || null);
        if (list.length && !sel) setSel(list[0].id);
      })
      .catch(() => {});
  }, [vendor, page, dateRange, billingFilters, sel]);

  useEffect(() => {
    fetchOrders();
    api.get('/billing/payment-stats').then(r => setStats(r.data)).catch(() => {});
    api.get('/billing/service-fee-stats').then(r => setFeeStats(r.data)).catch(() => {});
  }, [vendor]); // eslint-disable-line

  useEffect(() => { fetchOrders(); }, [page, dateRange, billingFilters]); // eslint-disable-line

  // Poll for new cash-pending orders so staff don't have to refresh manually
  useEffect(() => {
    const t = setInterval(fetchOrders, 10000);
    return () => clearInterval(t);
  }, [fetchOrders]);

  const cashPendingOrders = orders.filter(
    (o) => o.paymentMode === 'CASH' && o.paymentStatus !== 'PAID' && o.status !== 'CANCELLED'
  );

  const minutesAgo = (createdAt) => Math.floor((Date.now() - new Date(createdAt).getTime()) / 60000);

  const confirmCash = async (orderId) => {
    setConfirming(orderId);
    setCashError('');
    try {
      await api.post(`/billing/confirm-cash/${orderId}`);
      fetchOrders();
    } catch (err) {
      setCashError(err.response?.data?.error || 'Failed to confirm cash payment');
    } finally {
      setConfirming(null);
    }
  };

  const cancelCashOrder = async (orderId) => {
    setConfirming(orderId);
    setCashError('');
    try {
      await api.patch(`/orders/${orderId}/cancel-unpaid`);
      fetchOrders();
    } catch (err) {
      setCashError(err.response?.data?.error || 'Failed to cancel order');
    } finally {
      setConfirming(null);
    }
  };

  const order = orders.find((o) => o.id === sel);

  const handlePaySuccess = ({ gateway, cashPending } = {}) => {
    setShowPayment(false);
    if (cashPending) {
      // Cash isn't collected yet — don't show a receipt or mark paid.
      setActiveOrder(null);
      fetchOrders();
      return;
    }
    // Show receipt immediately with known-good status, then refresh orders
    setActiveOrder((prev) => prev ? { ...prev, paymentStatus: 'PAID', paymentMode: gateway || prev.paymentMode } : null);
    setShowReceipt(true);
    fetchOrders();
  };

  const openPayment = (o) => { setActiveOrder(o); setShowPayment(true); };
  const openReceipt = (o) => { setActiveOrder(o); setShowReceipt(true); };

  const statusColor = (s) =>
    s === 'PAID'    ? 'var(--green)' :
    s === 'FAILED'  ? 'var(--red)'   :
    s === 'PENDING' ? 'var(--accent)': 'var(--text2)';

  return (
    <div className="page">
      <div className="page-title">Billing & Payment</div>
      <div className="page-sub">Generate invoices, process payments and print receipts</div>

      {/* ── Cash Pending ── */}
      <div style={{
        background: '#3d2e10', border: '1px solid var(--accent)',
        borderRadius: 'var(--radius)', padding: 18, marginBottom: 24,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>💵 Cash Pending</div>
          <span style={{
            fontSize: 12, fontWeight: 700, padding: '2px 9px', borderRadius: 20,
            background: 'var(--accent)', color: '#1a1408',
          }}>
            {cashPendingOrders.length}
          </span>
        </div>

        {cashError && (
          <div style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, background: '#3d1a1a', color: 'var(--red)', fontSize: 13 }}>
            ❌ {cashError}
          </div>
        )}

        {cashPendingOrders.length === 0 ? (
          <div style={{ color: 'var(--text3)', fontSize: 13 }}>No pending cash orders</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {cashPendingOrders.map((o) => {
              const age = minutesAgo(o.createdAt);
              const stale = age >= 15;
              return (
                <div key={o.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 14, padding: '12px 16px', borderRadius: 'var(--radius-sm)',
                  background: stale ? '#3d1a1a' : 'var(--bg2)',
                  border: `1px solid ${stale ? 'var(--red)' : 'var(--border)'}`,
                }}>
                  <div>
                    <div style={{ fontWeight: 700, color: 'var(--accent)', fontSize: 15 }}>
                      {o.orderNumber}
                      {o.tableNo && <span style={{ fontWeight: 400, color: 'var(--text2)', fontSize: 12, marginLeft: 8 }}>🪑 Table {o.tableNo}</span>}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
                      {age <= 0 ? 'just now' : `${age} min ago`}
                      {o.items?.length ? ` · ${o.items.length} item${o.items.length > 1 ? 's' : ''}: ${o.items.map(i => i.name).join(', ')}` : ''}
                    </div>
                    {stale && (
                      <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 4 }}>
                        ⚠ Customer may have abandoned — consider cancelling
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>AED {Number(o.total).toFixed(2)}</span>
                    {stale && (
                      <button className="btn btn-ghost" disabled={confirming === o.id}
                        onClick={() => cancelCashOrder(o.id)}>
                        Cancel
                      </button>
                    )}
                    <button className="btn btn-primary" disabled={confirming === o.id}
                      onClick={() => confirmCash(o.id)}>
                      ✅ Cash Received
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid2">

        {/* ── Order selector ── */}
        <div>
          <div className="section-label" style={{ marginBottom: 12 }}>Select Order</div>
          <ListFilters
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            maxRangeDays={2}
            onDateChange={({ startDate, endDate }) => { setDateRange({ startDate, endDate }); setPage(1); setSel(null); }}
            filters={[
              {
                key: 'paymentStatus', label: 'Payment Status', value: billingFilters.paymentStatus,
                options: ['', 'PAID', 'PENDING', 'FAILED'],
                onChange: v => { setBillingFilters(f => ({ ...f, paymentStatus: v })); setPage(1); },
              },
              {
                key: 'paymentMode', label: 'Payment Mode', value: billingFilters.paymentMode,
                options: ['', 'CARD', 'CASH', 'UPI', 'WALLET', 'TAP'],
                onChange: v => { setBillingFilters(f => ({ ...f, paymentMode: v })); setPage(1); },
              },
            ]}
            onExport={() => downloadCSV('/vendors/me/reports/daily/csv', dateRange, 'billing-orders.csv')}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {orders.length === 0 && (
              <div style={{ color: 'var(--text3)', fontSize: 13, padding: 20, textAlign: 'center' }}>
                No records found for this period
              </div>
            )}
            {orders.map((o) => (
              <div key={o.id} onClick={() => setSel(o.id)}
                style={{
                  padding: '12px 16px', borderRadius: 'var(--radius-sm)',
                  border: '1px solid',
                  borderColor: sel === o.id ? 'var(--accent)' : 'var(--border)',
                  background: sel === o.id ? '#3d2e10' : 'var(--bg2)',
                  cursor: 'pointer',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                <div>
                  <span style={{ fontWeight: 700, color: 'var(--accent)' }}>{o.orderNumber}</span>
                  <span style={{ fontSize: 12, color: 'var(--text2)', marginLeft: 8 }}>
                    {new Date(o.createdAt).toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 700 }}>AED {Number(o.total).toFixed(2)}</span>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '3px 8px',
                    borderRadius: 20, background: 'var(--bg3)',
                    color: statusColor(o.paymentStatus),
                  }}>
                    {o.paymentStatus}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <Pagination
            page={pagination?.page || page}
            totalPages={pagination?.totalPages || 0}
            total={pagination?.total || 0}
            limit={pagination?.limit || 50}
            onPageChange={p => setPage(p)}
          />
        </div>

        {/* ── Invoice panel ── */}
        {order && (
          <div style={{
            background: 'var(--bg2)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', padding: 24, maxWidth: 480,
          }}>
            {/* Invoice header */}
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>⚡ QuickServe Kiosk</div>
              <div style={{ fontSize: 12, color: 'var(--text2)' }}>Order {order.orderNumber}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>{new Date().toLocaleString()}</div>
            </div>

            <div className="divider" />

            {/* Line items */}
            {order.items?.map((item, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between',
                fontSize: 14, padding: '6px 0', borderBottom: '1px solid var(--bg3)',
              }}>
                <span>{item.name} × {item.quantity || item.qty}</span>
                <span>AED {(item.price * (item.quantity || item.qty)).toFixed(2)}</span>
              </div>
            ))}

            <div className="divider" />

            {/* Totals */}
            {[['Subtotal', order.subtotal], ['VAT 5%', order.tax]].map(([l, v]) => (
              <div key={l} style={{
                display: 'flex', justifyContent: 'space-between',
                fontSize: 14, padding: '6px 0', color: 'var(--text2)',
              }}>
                <span>{l}</span><span>AED {Number(v || 0).toFixed(2)}</span>
              </div>
            ))}

            {order.discount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '6px 0', color: 'var(--green)' }}>
                <span>Discount</span><span>- AED {Number(order.discount).toFixed(2)}</span>
              </div>
            )}

            <div style={{
              display: 'flex', justifyContent: 'space-between',
              fontWeight: 700, fontSize: 16,
              marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)',
            }}>
              <span>Total</span>
              <span style={{ color: 'var(--accent)' }}>AED {Number(order.total).toFixed(2)}</span>
            </div>

            <div className="divider" />

            {/* Payment status + action */}
            {order.paymentStatus === 'PAID' ? (
              <div>
                <div style={{
                  textAlign: 'center', padding: 14,
                  background: '#1a3d2a', borderRadius: 'var(--radius-sm)',
                  color: 'var(--green)', fontWeight: 700, fontSize: 15, marginBottom: 10,
                }}>
                  ✅ Payment Confirmed
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => openReceipt(order)}>
                    🖨 Print Receipt
                  </button>
                  <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => {
                    const url = `${process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '')}/api/receipts/${order.id}/html`;
                    window.open(url, '_blank');
                  }}>
                    📄 Open PDF
                  </button>
                </div>
              </div>
            ) : order.paymentStatus === 'REFUNDED' ? (
              <div style={{
                textAlign: 'center', padding: 14,
                background: '#3d1a1a', borderRadius: 'var(--radius-sm)',
                color: 'var(--red)', fontWeight: 700,
              }}>
                ↩ Refunded
              </div>
            ) : (
              <button
                className="btn btn-primary"
                style={{ width: '100%', padding: 14, fontSize: 15 }}
                onClick={() => openPayment(order)}
              >
                💳 Pay AED {Number(order.total).toFixed(2)}
              </button>
            )}

            {/* Receipt URL if available */}
            {order.payments?.[0]?.receiptUrl && (
              <a
                href={order.payments[0].receiptUrl}
                target="_blank"
                rel="noreferrer"
                style={{ display: 'block', textAlign: 'center', marginTop: 10, fontSize: 12, color: 'var(--blue)' }}>
                View Receipt ↗
              </a>
            )}
          </div>
        )}
      </div>

      {/* ── Payment Analytics ── */}
      {stats && (
        <div style={{ marginTop: 28 }}>
          <div className="page-title" style={{ fontSize: 16, marginBottom: 4 }}>Payment Analytics</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>Last 30 days</div>

          {/* Top stats row */}
          <div className="grid4" style={{ marginBottom: 20 }}>
            {/* Success rate — coloured by threshold */}
            <div className="stat-card" style={{ gridColumn: 'span 1' }}>
              <div className="stat-label">Success Rate</div>
              <div className="stat-val" style={{
                color: stats.successRate >= 95 ? 'var(--green)'
                     : stats.successRate >= 85 ? 'var(--accent)'
                     : 'var(--red)',
              }}>
                {stats.successRate}%
              </div>
            </div>
            {[
              ['Attempts',  stats.totalAttempts],
              ['Success',   stats.totalSuccess],
              ['Failed',    stats.totalFailed],
            ].map(([label, val]) => (
              <div key={label} className="stat-card">
                <div className="stat-label">{label}</div>
                <div className="stat-val">{val}</div>
              </div>
            ))}
          </div>

          {/* Service fee stats row */}
          {feeStats && feeStats.orderCount > 0 && (
            <div className="grid4" style={{ marginBottom: 20 }}>
              <div className="stat-card">
                <div className="stat-label">Service Fees Collected</div>
                <div className="stat-val" style={{ color: 'var(--green)' }}>AED {feeStats.totalCollected.toFixed(2)}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Platform Share</div>
                <div className="stat-val">AED {feeStats.platformShare.toFixed(2)}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Mall Share</div>
                <div className="stat-val">AED {feeStats.mallShare.toFixed(2)}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Avg Fee / Order</div>
                <div className="stat-val">AED {feeStats.averageFee.toFixed(2)}</div>
              </div>
            </div>
          )}

          <div className="grid2">
            {/* Payment mode breakdown */}
            <div className="card">
              <div className="section-label" style={{ marginBottom: 12 }}>By Payment Mode</div>
              {Object.entries(stats.byMode || {}).length === 0
                ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>No data yet</div>
                : Object.entries(stats.byMode).map(([mode, d]) => {
                    const pct = d.total > 0 ? Math.round((d.success / d.total) * 100) : 0;
                    return (
                      <div key={mode} style={{ marginBottom: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                          <span style={{ fontWeight: 600 }}>{mode}</span>
                          <span style={{ color: 'var(--text3)' }}>{d.success}/{d.total} ({pct}%)</span>
                        </div>
                        <div style={{ height: 6, background: 'var(--bg3)', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: pct >= 95 ? 'var(--green)' : pct >= 80 ? 'var(--accent)' : 'var(--red)', borderRadius: 4, transition: 'width .4s' }} />
                        </div>
                      </div>
                    );
                  })}
            </div>

            {/* Top failure reasons */}
            <div className="card">
              <div className="section-label" style={{ marginBottom: 12 }}>Top Failure Reasons</div>
              {!stats.failureReasons?.length
                ? <div style={{ fontSize: 13, color: 'var(--green)' }}>✅ No failures recorded</div>
                : stats.failureReasons.map(({ reason, count }, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--bg3)', fontSize: 13 }}>
                      <span style={{ color: 'var(--text2)', flex: 1, marginRight: 12 }}>{reason}</span>
                      <span style={{ fontWeight: 700, color: 'var(--red)', background: '#3d1a1a', padding: '2px 10px', borderRadius: 20, fontSize: 12 }}>{count}</span>
                    </div>
                  ))}
              {stats.totalAbandoned > 0 && (
                <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text3)' }}>
                  + {stats.totalAbandoned} abandoned (customer left before paying)
                </div>
              )}
            </div>
          </div>

          {/* Hourly volume chart */}
          {stats.hourlyVolume?.some(v => v > 0) && (
            <div className="card" style={{ marginTop: 16 }}>
              <div className="section-label" style={{ marginBottom: 12 }}>Payment Volume by Hour</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 100 }}>
                {stats.hourlyVolume.map((cnt, hr) => {
                  const maxV = Math.max(...stats.hourlyVolume, 1);
                  return (
                    <div key={hr} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                      {cnt > 0 && <span style={{ fontSize: 9, color: 'var(--accent)' }}>{cnt}</span>}
                      <div style={{ width: '100%', background: cnt > 0 ? 'var(--accent)' : 'var(--bg3)', borderRadius: '3px 3px 0 0', height: `${(cnt / maxV) * 100}%`, minHeight: 3, opacity: .85 }} />
                      <span style={{ fontSize: 9, color: 'var(--text3)' }}>{hr}</span>
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6, textAlign: 'center' }}>Hour of day (0–23)</div>
            </div>
          )}
        </div>
      )}

      {/* Payment Modal */}
      {showPayment && activeOrder && (
        <PaymentModal
          order={activeOrder}
          onSuccess={handlePaySuccess}
          onClose={() => { setShowPayment(false); setActiveOrder(null); }}
        />
      )}

      {/* Receipt Modal */}
      {showReceipt && activeOrder && (
        <Receipt
          order={activeOrder}
          vendor={vendor}
          onClose={() => { setShowReceipt(false); setActiveOrder(null); }}
        />
      )}
    </div>
  );
}
